import {
  AttachmentIcon,
  CircularProgress,
  CloseIcon,
  CompassGoToIcon,
  cyan,
  FaroIconButton,
  FaroText,
  FaroTooltip,
  MeasurementIcon,
  grey,
  NO_TRANSLATE_CLASS,
  ShareLinkIcon,
} from "@faro-lotv/flat-ui";
import { CardHeader, Stack } from "@mui/material";
import { MouseEvent, useCallback, useMemo, type JSX } from "react";
import { AnnotationViewerMenu } from "./annotation-viewer-menu";
import type { AnnotationViewerProps } from "./annotation-viewer-types";

/**
 * @returns UI showing the header of an annotation differently, depending on the variant
 */
export function AnnotationViewerHeader({
  canEdit,
  canDelete,
  hasAttachment,
  links,
  isMeasurement,
  title,
  size,
  isLoading,
  variant,
  externalAnnotationViewerProps,
  onCloseButtonClick,
  onDelete,
  onEdit,
  onShareButtonClick,
  onGoToButtonClick,
  onCopyToClipboard,
  onContextMenuOpen,
  markupCoordinateString,
  onCopyMarkupCoordinates,
}: Pick<
  AnnotationViewerProps,
  | "canEdit"
  | "canDelete"
  | "hasAttachment"
  | "links"
  | "isMeasurement"
  | "title"
  | "size"
  | "variant"
  | "externalAnnotationViewerProps"
  | "onCloseButtonClick"
  | "onDelete"
  | "onEdit"
  | "onShareButtonClick"
  | "onGoToButtonClick"
  | "onCopyToClipboard"
  | "onContextMenuOpen"
  | "markupCoordinateString"
  | "onCopyMarkupCoordinates"
  | "isLoading"
>): JSX.Element {
  const closeAnnotation = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onCloseButtonClick();
    },
    [onCloseButtonClick],
  );

  const iconSx = useMemo(
    () => ({
      width: variant === "title" ? "18px" : "20px",
      height: variant === "title" ? "18px" : "20px",
      color: grey[0],
    }),
    [variant],
  );

  return (
    <CardHeader
      aria-label="annotation title"
      // Avoid the title from being wrapped in a typography component
      disableTypography
      sx={{
        ".MuiCardHeader-content": {
          overflow: "hidden",
          // Reduce space between title's lines
          lineHeight: "1rem",
        },
        ".MuiCardHeader-action": {
          alignSelf: "flex-start",
          m: 0,
        },
        ...(variant === "title" && { py: 0.3, px: 1 }),
        ...(variant !== "title" && !!size && { minWidth: size.minWidth }),
      }}
      avatar={
        !isMeasurement &&
        !hasAttachment &&
        !links &&
        !(variant === "title" && externalAnnotationViewerProps) ? null : (
          <>
            {externalAnnotationViewerProps?.logo}
            {isMeasurement && <MeasurementIcon sx={iconSx} />}
            {(hasAttachment || links) && <AttachmentIcon sx={iconSx} />}
          </>
        )
      }
      action={
        variant === "full" ? (
          <Stack
            direction="row"
            sx={{ gap: 0.75 }}
            // Prevents the click from propagating to the annotation box
            // This is needed to avoid the annotation box accidentally being expanded when clicking on the buttons
            onPointerDown={(e) => e.stopPropagation()}
          >
            {(canEdit ||
              canDelete ||
              !!onCopyToClipboard ||
              !!markupCoordinateString) && (
              <AnnotationViewerMenu
                canEdit={canEdit}
                canDelete={canDelete}
                onCopyToClipboard={onCopyToClipboard}
                markupCoordinateString={markupCoordinateString}
                onCopyMarkupCoordinates={onCopyMarkupCoordinates}
                onDelete={onDelete}
                onEdit={onEdit}
                onContextMenuOpen={onContextMenuOpen}
              />
            )}
            <FaroTooltip title="Share a link to the annotation">
              <FaroIconButton
                size="s"
                margin={0}
                aria-label="share annotation"
                dark
                onClick={onShareButtonClick}
              >
                <ShareLinkIcon />
              </FaroIconButton>
            </FaroTooltip>
            <FaroTooltip title="Go to annotation">
              <FaroIconButton
                size="s"
                margin={0}
                aria-label="go to annotation"
                dark
                onClick={onGoToButtonClick}
              >
                <CompassGoToIcon />
              </FaroIconButton>
            </FaroTooltip>
            <FaroIconButton
              size="s"
              margin={0}
              aria-label="close annotation"
              dark
              onClick={closeAnnotation}
            >
              <CloseIcon />
            </FaroIconButton>
          </Stack>
        ) : undefined
      }
      title={
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          {isLoading && (
            <CircularProgress
              sx={{
                color: cyan[500],
              }}
              size="14px"
            />
          )}
          {variant === "title" ? (
            <FaroText
              variant="bodyM"
              className={NO_TRANSLATE_CLASS}
              sx={{
                color: externalAnnotationViewerProps?.error?.color ?? grey[0],
                wordBreak: "break-word",
                overflowWrap: "break-word",
                pl: 0.75,
              }}
              shouldElide
            >
              {title}
            </FaroText>
          ) : (
            <FaroText
              variant="heading14"
              className={NO_TRANSLATE_CLASS}
              sx={{
                color: externalAnnotationViewerProps?.error?.color ?? grey[0],
                wordWrap: "break-word",
                verticalAlign: "top",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {title}
            </FaroText>
          )}
        </Stack>
      }
    />
  );
}
